import { MouseEvent, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from 'components/IconButton/IconButton';
import { getCardMiniActionButtonsConfig } from 'components/SelectCardWrapper/config/cardMiniActionButtonDefs';

import { CardActions } from 'constants/enums';

export type ResolvedCardMenuAction = {
  action?: CardActions;
  onClick: () => void;
};

/**
 * Maps {@link useCardActions} dropdown `actions` to a row of {@link IconButton}s
 * using {@link getCardMiniActionButtonsConfig}.
 */
export const useMapCardActionsToIconButtons = (
  actions: Array<ResolvedCardMenuAction | null | undefined>
): ReactNode[] => {
  const { t } = useTranslation();
  const actionButtonsConfig = useMemo(
    () => getCardMiniActionButtonsConfig(t),
    [t]
  );

  const actionButtons = useMemo(
    () =>
      actions
        .map((action) => {
          if (!action?.action) {
            return null;
          }

          const buttonConfig = actionButtonsConfig[action.action];

          if (!buttonConfig) {
            return null;
          }

          return (
            <IconButton
              key={action.action}
              icon={buttonConfig.icon}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                action.onClick();
              }}
              tooltip={buttonConfig.tooltip}
              type={buttonConfig.type}
            />
          );
        })
        .filter(Boolean) as ReactNode[],
    [actions, actionButtonsConfig]
  );

  return actionButtons;
};
